import type { PlatformServices, TenantListQuery } from '../platformContracts';
import type { FeatureKey, Page, UserRole } from '@/types';
import type { FeatureDef } from '@/lib/featureRegistry';
import type {
  AuditLogEntry, FeatureAdoptionRow, Plan, PlanInput, PlatformActor, PlatformAnalyticsReport, PlatformDashboardData,
  PlatformPayment, PlatformSettings, Subscription, TenantDetail, TenantFeatureState, TenantListRow,
  TenantOnboardingInput, TenantStatus,
} from '@/types/platform';
import type { RangeKey, TeamMember } from '@/types/admin';
import { api, qs } from './client';

const P = '/api/platform';

export const httpPlatformServices: PlatformServices = {
  session: {
    me: () => api<PlatformActor>(`${P}/me`),
  },
  dashboard: {
    summary: (range: RangeKey) => api<PlatformDashboardData>(`${P}/dashboard${qs({ range })}`),
  },
  tenants: {
    list: (q: TenantListQuery) => api<Page<TenantListRow>>(`${P}/tenants${qs({ ...q })}`),
    get: (id) => api<TenantDetail>(`${P}/tenants/${id}`),
    create: (input: TenantOnboardingInput) => api<TenantDetail>(`${P}/tenants`, { method: 'POST', body: JSON.stringify(input) }),
    setStatus: (id, status: TenantStatus, reason?) => api<TenantDetail>(`${P}/tenants/${id}/status`, { method: 'POST', body: JSON.stringify({ status, reason }) }),
    setPlan: (id, planId) => api<TenantDetail>(`${P}/tenants/${id}/plan`, { method: 'POST', body: JSON.stringify({ planId }) }),
  },
  features: {
    registry: () => api<FeatureDef[]>(`${P}/features/registry`),
    adoption: () => api<FeatureAdoptionRow[]>(`${P}/features/adoption`),
    getState: (tenantId) => api<TenantFeatureState>(`${P}/features/${tenantId}`),
    setOverride: (tenantId, key: FeatureKey, value: boolean | null) =>
      api<TenantFeatureState>(`${P}/features/${tenantId}/override`, { method: 'POST', body: JSON.stringify({ key, value }) }),
    resetAll: (tenantId) => api<TenantFeatureState>(`${P}/features/${tenantId}/reset`, { method: 'POST' }),
  },
  plans: {
    list: () => api<(Plan & { tenantCount: number })[]>(`${P}/plans`),
    create: (input: PlanInput) => api<Plan>(`${P}/plans`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<Plan>(`${P}/plans/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    setActive: (id, active) => api<Plan>(`${P}/plans/${id}/active`, { method: 'POST', body: JSON.stringify({ active }) }),
    remove: (id) => api<void>(`${P}/plans/${id}`, { method: 'DELETE' }),
  },
  users: {
    list: (tenantId) => api<TeamMember[]>(`${P}/tenants/${tenantId}/users`),
    add: (tenantId, input: { name: string; email: string; role: UserRole }) =>
      api<TeamMember>(`${P}/tenants/${tenantId}/users`, { method: 'POST', body: JSON.stringify(input) }),
    setRole: (tenantId, memberId, role) =>
      api<TeamMember>(`${P}/tenants/${tenantId}/users/${memberId}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
    setStatus: (tenantId, memberId, status) =>
      api<TeamMember>(`${P}/tenants/${tenantId}/users/${memberId}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
    remove: (tenantId, memberId) => api<void>(`${P}/tenants/${tenantId}/users/${memberId}`, { method: 'DELETE' }),
  },
  subscriptions: {
    list: (q) => api<Page<Subscription>>(`${P}/subscriptions${qs({ ...q })}`),
    get: (tenantId) => api<Subscription>(`${P}/subscriptions/${tenantId}`),
    changePlan: (tenantId, planId, cycle) =>
      api<Subscription>(`${P}/subscriptions/${tenantId}/plan`, { method: 'POST', body: JSON.stringify({ planId, cycle }) }),
    cancel: (tenantId) => api<Subscription>(`${P}/subscriptions/${tenantId}/cancel`, { method: 'POST' }),
    reactivate: (tenantId) => api<Subscription>(`${P}/subscriptions/${tenantId}/reactivate`, { method: 'POST' }),
  },
  payments: {
    list: (q) => api<Page<PlatformPayment>>(`${P}/payments${qs({ ...q })}`),
    refund: (id, reason) => api<PlatformPayment>(`${P}/payments/${id}/refund`, { method: 'POST', body: JSON.stringify({ reason }) }),
  },
  analytics: {
    report: (range: RangeKey) => api<PlatformAnalyticsReport>(`${P}/analytics${qs({ range })}`),
  },
  audit: {
    list: (q) => api<Page<AuditLogEntry>>(`${P}/audit${qs({ ...q })}`),
  },
  settings: {
    get: () => api<PlatformSettings>(`${P}/settings`),
    save: (input) => api<PlatformSettings>(`${P}/settings`, { method: 'PUT', body: JSON.stringify(input) }),
  },
};
